/**
 * Hook for managing conversation refs
 * Consolidates all useRef declarations for the Conversation component
 */

import { useRef } from 'react';

interface Message {
  id?: string;
  from: string;
  text: string;
  channel: 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email';
  _createdDate: Date;
  isFromCustomer?: boolean;
  isSystemMessage?: boolean;
  avatarUrl?: string;
  extractedStaffInfo?: { staffName: string; avatarUrl?: string };
  senderStaffName?: string;
  senderStaffAvatar?: string;
  senderType?: string;
  senderStaffId?: string;
}

export interface ConversationRefs {
  // DOM refs
  titleCardRef: React.RefObject<HTMLDivElement | null>;
  scrollToBottomTimeoutRef: { current: ReturnType<typeof setTimeout> | null };
  hasScrolledToBottomRef: { current: boolean };

  // Pagination refs
  paginationCursorRef: { current: string | undefined };
  isLoadingOlderRef: { current: boolean };
  isPrefetchingRef: { current: boolean };
  has50PercentPrefetchedRef: { current: boolean };
  consecutiveDuplicateBatchesRef: { current: number };

  // Message refs
  messagesRef: { current: Message[] };
  sentMessagesRef: { current: Array<{ text: string; staffName: string; staffId: string; contactId: string; avatarUrl?: string }> };
  messageQueueRef: { current: Array<{ messages: unknown[] }> };

  // State tracking refs
  initialLoadDoneRef: { current: boolean };
  initialScrollDoneRef: { current: boolean };
  contactAvatarCacheRef: { current: Record<string, string | undefined> };
}

/**
 * Initialize all conversation refs
 * @returns ConversationRefs with all ref objects
 */
export const useConversationRefs = (): ConversationRefs => {
  const titleCardRef = useRef<HTMLDivElement>(null);
  const scrollToBottomTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasScrolledToBottomRef = useRef(false);

  const paginationCursorRef = useRef<string | undefined>(undefined);
  const isLoadingOlderRef = useRef(false);
  const isPrefetchingRef = useRef<boolean>(false);
  const has50PercentPrefetchedRef = useRef<boolean>(false);
  const consecutiveDuplicateBatchesRef = useRef<number>(0);

  const messagesRef = useRef<Message[]>([]);
  const sentMessagesRef = useRef<Array<{ text: string; staffName: string; staffId: string; contactId: string; avatarUrl?: string }>>([]);
  const messageQueueRef = useRef<Array<{ messages: unknown[] }>>([]);

  const initialLoadDoneRef = useRef(false);
  const initialScrollDoneRef = useRef(false);
  const contactAvatarCacheRef = useRef<Record<string, string | undefined>>({});

  return {
    titleCardRef,
    scrollToBottomTimeoutRef,
    hasScrolledToBottomRef,
    paginationCursorRef,
    isLoadingOlderRef,
    isPrefetchingRef,
    has50PercentPrefetchedRef,
    consecutiveDuplicateBatchesRef,
    messagesRef,
    sentMessagesRef,
    messageQueueRef,
    initialLoadDoneRef,
    initialScrollDoneRef,
    contactAvatarCacheRef,
  };
};
